import { useAuth } from '../../context/AuthContext'
import { useState, useEffect } from 'react'
import { useWedding } from '../../context/WeddingContext'
import { supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { Calendar, Users, DollarSign, Clock, ChevronRight, Crown, Mail, Gift, Camera, Copy, Share2 } from 'lucide-react'
import MemoryReportCard from './memories/MemoryReportCard'
import { loveNote } from '../../lib/loveNotes'
import { celebrateOnce } from '../../lib/celebrate'
import { Sprig } from '../../components/ornaments'
import QuestionOfWeek from '../../components/QuestionOfWeek'

function daysUntil(d) {
  if (!d) return null
  const diff = new Date(d) - new Date()
  return Math.floor(diff / 86400000)
}



export default function DashboardHome() {
  const {user, profile } = useAuth()
  const { wedding, events, isPremium } = useWedding()

  const navigate               = useNavigate()
  const [budgetSpent, setBudgetSpent]   = useState(0)
  const [weddingCurrency, setWeddingCurrency] = useState('NGN')
  const [totalBudget, setTotalBudget]   = useState(0)
  const [guestCount, setGuestCount]     = useState(0)
  const [confirmedCount, setConfirmed]  = useState(0)
  const [nextTasks, setNextTasks]       = useState([])
  const [todoProgress, setTodoProgress] = useState({ done: 0, total: 16 })
  const [walletBal, setWalletBal]       = useState(0)
  const [refCopied, setRefCopied]       = useState(false)

  useEffect(() => {
    if (!wedding?.id) return
    // Fetch real budget spent + total budget from weddings table
    ;(async () => {
      // Get total budget from the wedding record
      const { data: weddingData } = await supabase
        .from('weddings').select('total_budget, currency').eq('id', wedding.id).maybeSingle()
      if (weddingData?.total_budget) setTotalBudget(Number(weddingData.total_budget))
      if (weddingData?.currency)    setWeddingCurrency(weddingData.currency)

      // Get all committed expenses (sum of budget_items)
      const { data: cats } = await supabase
        .from('budget_categories').select('id').eq('wedding_id', wedding.id)
      if (cats?.length) {
        const { data: items } = await supabase
          .from('budget_items').select('amount').in('category_id', cats.map(c=>c.id))
        const total = (items||[]).reduce((s,i) => s+Number(i.amount||0), 0)
        setBudgetSpent(total)
      }
    })()
    // Fetch guest count
    ;(async () => {
      const { data: g } = await supabase.from('guests').select('id, plus_one').eq('wedding_id', wedding.id)
      const count = (g||[]).reduce((s,guest) => s+(guest.plus_one?2:1), 0)
      setGuestCount(count)
      const { count: conf } = await supabase.from('guests').select('*',{count:'exact',head:true})
        .eq('wedding_id', wedding.id).in('rsvp_status',['attending','confirmed'])
      setConfirmed(conf||0)
      if ((conf||0) >= 1) celebrateOnce(`firstYes:${wedding.id}`, { title: 'Your first yes! 🎉', note: 'Someone you love just confirmed they\'ll be there to celebrate you.' })
    })()
  }, [wedding?.id])

  const days = daysUntil(wedding?.wedding_date)

  // Gentle celebration when the couple crosses a countdown milestone
  useEffect(() => {
    if (!wedding?.id || days == null) return
    const MILESTONES = [100, 50, 30, 10, 7, 1]
    if (MILESTONES.includes(days)) {
      const firstName = wedding?.partner1_name?.split(' ')[0] || 'lovebirds'
      celebrateOnce(`days${days}:${wedding.id}`, {
        title: days === 1 ? 'One day to go! 🎉' : `${days} days to forever`,
        note: days === 1 ? `Tomorrow, ${firstName}, everything you planned comes alive.` : 'Every plan is bringing you closer.',
      })
    }
  }, [wedding?.id, days])

  // Total prebuilt tasks must match the CHECKLIST in TodoTab.jsx
  // 12+(6) + 6-9(6) + 3-6(5) + 1-3(5) + final(5) = 27
  const TOTAL_PREBUILT = 27

  // Load upcoming tasks + progress
  useEffect(()=>{
    if (!wedding?.id) return
    Promise.all([
      supabase.from('weddings').select('todo_completed').eq('id',wedding.id).maybeSingle(),
      supabase.from('todo_items').select('*').eq('wedding_id',wedding.id).eq('completed',false).order('created_at').limit(10),
      supabase.from('custom_tasks').select('*',{count:'exact',head:true}).eq('wedding_id',wedding.id),
    ]).then(([{data:w},{data:ct},{count:customCount}])=>{
      const completed = w?.todo_completed||[]
      const total = TOTAL_PREBUILT + (customCount||0)
      setTodoProgress({ done: Math.min(completed.length, total), total })
      const PREBUILT = {
        '12+':[{id:'b1',title:'Set your total wedding budget'},{id:'b2',title:'Choose your wedding date'},{id:'b3',title:'Create all your wedding events'},{id:'b4',title:'Start building your guest list'}],
        '6-9':[{id:'c1',title:'Send Save the Dates'},{id:'c2',title:'Book photographer & videographer'},{id:'c3',title:'Create your gift registry'}],
        '3-6':[{id:'d1',title:'Send formal invitations'},{id:'d2',title:'Finalise guest list'},{id:'d3',title:'Build the event programme'}],
        '1-3':[{id:'e1',title:'Write your vows & speeches'},{id:'e2',title:'Confirm all vendors'},{id:'e3',title:'Review & finalise budget'}],
        'final':[{id:'f1',title:'Confirm final guest RSVPs'},{id:'f2',title:'Set up guest check-in'},{id:'f3',title:'Start logging wedding gifts'}],
      }

      // Determine which bucket matches the current days remaining
      const daysLeft = wedding?.wedding_date
        ? Math.ceil((new Date(wedding.wedding_date) - new Date()) / (1000*60*60*24))
        : 999
      function currentBucket(d) {
        if (d > 270) return '12+'
        if (d > 180) return '6-9'
        if (d > 90)  return '3-6'
        if (d > 30)  return '1-3'
        return 'final'
      }
      const bucket = currentBucket(daysLeft)
      // Start from the relevant bucket, overflow to next if needed
      const ALL_BUCKETS = ['12+','6-9','3-6','1-3','final']
      const startIdx = ALL_BUCKETS.indexOf(bucket)
      const orderedBuckets = ALL_BUCKETS.slice(startIdx) // current bucket first

      // Get top 2 incomplete prebuilt tasks from the right bucket
      const incomplete = []
      for (const b of orderedBuckets) {
        for (const t of PREBUILT[b]||[]) {
          if (!completed.includes(t.id)) incomplete.push({...t, bucket:b, isPrebuilt:true})
          if (incomplete.length >= 2) break
        }
        if (incomplete.length >= 2) break
      }
      // Fill with custom tasks from DB if still under 2
      const combined = [...incomplete, ...(ct||[])].slice(0,2)
      setNextTasks(combined.map(t => t.title))
    })
  },[wedding?.id])

  if (!wedding) return (
    <div style={{ textAlign:'center', padding:'3rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>One moment, pulling up your plans…</p>
    </div>
  )

  // Use first name only, or both couples if partner2 exists
  const firstName = (wedding?.partner1_name || profile?.full_name || '').split(' ')[0] || 'there'
  const partnerFirst = wedding?.partner2_name?.split(' ')[0]
  const name = partnerFirst ? `${firstName} & ${partnerFirst}` : firstName
  const CURRENCY_SYMBOLS = { NGN:'₦', USD:'$', GBP:'£', EUR:'€', GHS:'₵', KES:'KSh', ZAR:'R' }
  const currency = CURRENCY_SYMBOLS[weddingCurrency] || '₦'

  const stats = [
    { label:'Events', value:events?.length||0, icon:Calendar, color:'#9BAF9B', bg:'rgba(155,175,155,0.12)', link:'/dashboard/events' },
    { label:'Guests', value:guestCount, icon:Users, color:'#C4826A', bg:'rgba(196,130,106,0.1)', link:'/dashboard/guests' },
    { label:'Budget Spent', value:`${currency}${budgetSpent.toLocaleString()}`, icon:DollarSign, color:'#9BAF9B', bg:'rgba(155,175,155,0.12)', link:'/dashboard/budget' },
  ]

  const quickActions = [
    { label:'Design your invitation', desc:'Create & send invites', to:'/dashboard/events', icon:Mail },
    { label:'Add your guests', desc:'Build your guest list', to:'/dashboard/guests', icon:Users },
    { label:'Set up your registry', desc:'Add gift wishes', to:'/dashboard/registry', icon:Gift },
    { label:'Upload studio photos', desc:'Add your wedding photos', to:'/dashboard/profile', icon:Camera },
  ]

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.5rem' }}>

      {/* Welcome */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:'0.55rem', marginBottom:'0.3rem' }}>
            <Sprig size={22}/>
            <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.4rem,3vw,1.9rem)', color:'#1A1A1A', margin:0 }}>
              Hello, {name}
            </h1>
          </div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', margin:0, fontStyle:'italic' }}>
            {loveNote(wedding)}
          </p>
          {wedding?.founding && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:'0.35rem', marginTop:'0.5rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.64rem', fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase', color:'#8A6D3B', background:'rgba(212,175,55,0.14)', border:'1px solid rgba(212,175,55,0.4)', borderRadius:100, padding:'0.3rem 0.75rem' }}>
              <Crown size={11} color="#C9A84C"/> Founding Couple
            </span>
          )}
        </div>
        {!isPremium && (
          <button onClick={()=>navigate('/pricing')} style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'linear-gradient(135deg,#C4826A,#9BAF9B)', color:'white', border:'none', borderRadius:100, padding:'0.6rem 1.25rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem' }}>
            <Crown size={14}/> Upgrade
          </button>
        )}
      </div>

      {/* Countdown */}
      {days !== null && (
        <div onClick={()=>navigate('/dashboard/programme')} style={{ background:'linear-gradient(145deg,#7A3D28 0%,#A85A3C 28%,#C4826A 58%,#E8B09A 82%,#F5DED4 100%)', borderRadius:'1.5rem', padding:'1.5rem 1.75rem', position:'relative', overflow:'hidden', cursor:'pointer' }}>

          {/* Texture overlay */}
          <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', opacity:0.04, pointerEvents:'none' }} preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="htex" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1" fill="#5A2010"/>
                <circle cx="0" cy="0" r="1" fill="#5A2010"/>
                <circle cx="40" cy="0" r="1" fill="#5A2010"/>
                <circle cx="0" cy="40" r="1" fill="#5A2010"/>
                <circle cx="40" cy="40" r="1" fill="#5A2010"/>
                <path d="M20 4 Q22 10 20 16 Q18 10 20 4Z" fill="#5A2010" opacity="0.7"/>
                <path d="M4 20 Q10 22 16 20 Q10 18 4 20Z" fill="#5A2010" opacity="0.7"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#htex)"/>
          </svg>

          {/* Top glow */}
          <div style={{ position:'absolute', top:-40, right:-20, width:180, height:120, background:'radial-gradient(ellipse, rgba(245,222,212,0.2) 0%, transparent 70%)', pointerEvents:'none' }}/>

          <div style={{ position:'relative', zIndex:2, display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap' }}>

            {/* Left — ornament + label */}
            <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
              {/* Mini animated ring */}
              <svg width="44" height="44" viewBox="0 0 44 44" style={{ animation:'spin-slow 12s linear infinite', flexShrink:0 }}>
                {[0,45,90,135,180,225,270,315].map((deg,i) => (
                  <circle key={deg} cx={22 + 17*Math.cos((deg-90)*Math.PI/180)} cy={22 + 17*Math.sin((deg-90)*Math.PI/180)}
                    r={i%2===0?2:1.3} fill={i%2===0?"rgba(245,222,212,0.85)":"rgba(245,222,212,0.4)"}/>
                ))}
                <circle cx="22" cy="22" r="7" fill="none" stroke="rgba(245,222,212,0.25)" strokeWidth="0.8"/>
                <circle cx="22" cy="22" r="3" fill="rgba(245,222,212,0.7)"/>
              </svg>

              <div>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:700, color:'rgba(255,235,220,0.65)', letterSpacing:'0.14em', textTransform:'uppercase', margin:'0 0 0.15rem' }}>Your Big Day</p>
                {/* Big days number */}
                <div style={{ display:'flex', alignItems:'baseline', gap:'0.5rem' }}>
                  <style>{'@keyframes day-pulse { 0%,100%{transform:scale(1);opacity:0.97} 50%{transform:scale(1.06);opacity:1} } @keyframes spin-slow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }'}</style>
                  {days >= 0 && (
                    <span style={{ fontFamily:'Gibs, serif', fontSize:'clamp(2.8rem,7vw,3.8rem)', color:'rgba(255,248,244,0.97)', lineHeight:1, fontWeight:400, letterSpacing:'-0.04em', textShadow:'0 2px 12px rgba(80,20,5,0.2)', display:'inline-block', animation:'day-pulse 1s ease-in-out infinite' }}>
                      {days === 0 ? '🎉' : days}
                    </span>
                  )}
                  {days > 0 && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'rgba(255,235,220,0.6)', fontWeight:400, letterSpacing:'0.1em', textTransform:'uppercase' }}>days to go</span>}
                  {days === 0 && <span style={{ fontFamily:'Gibs, serif', fontSize:'1rem', color:'rgba(255,248,244,0.9)' }}>Today is your day!</span>}
                  {days < 0 && <span style={{ fontFamily:'Gibs, serif', fontSize:'clamp(1.35rem,4.5vw,2.4rem)', color:'rgba(255,248,244,0.97)', lineHeight:1.15, fontWeight:400, letterSpacing:'-0.01em' }}>Congratulations!</span>}
                </div>
              </div>
            </div>

            {/* Right — date + venue */}
            <div style={{ textAlign:'right' }}>
              {/* Decorative divider (vertical on desktop) */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'0.2rem' }}>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'rgba(255,240,232,0.8)', margin:0, letterSpacing:'0.02em' }}>
                  {wedding?.wedding_date && new Date(wedding.wedding_date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}
                </p>
                {wedding?.venue && (
                  <>
                    <div style={{ width:24, height:'0.5px', background:'rgba(255,235,220,0.3)', marginLeft:'auto' }}/>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'rgba(255,235,220,0.5)', margin:0 }}>{wedding.venue}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Countdown — no date set yet */}
      {days === null && (
        <div onClick={()=>navigate('/dashboard/settings')} style={{ background:'linear-gradient(145deg,#7A3D28 0%,#A85A3C 28%,#C4826A 58%,#E8B09A 82%,#F5DED4 100%)', borderRadius:'1.5rem', padding:'1.5rem 1.75rem', position:'relative', overflow:'hidden', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', flexWrap:'wrap' }}>
          <div>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.6rem', fontWeight:700, color:'rgba(255,235,220,0.65)', letterSpacing:'0.14em', textTransform:'uppercase', margin:'0 0 0.3rem' }}>Your Big Day</p>
            <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', color:'rgba(255,248,244,0.97)', margin:0, letterSpacing:'-0.02em' }}>When are you saying "I do"?</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'rgba(255,235,220,0.8)', margin:'0.3rem 0 0' }}>Set your wedding date to start the countdown.</p>
          </div>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:700, color:'#7A3D28', background:'rgba(255,248,244,0.92)', borderRadius:100, padding:'0.5rem 1.1rem', whiteSpace:'nowrap' }}>Set the date →</span>
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'0.875rem' }}>

        {/* ── Events card ── */}
        <div onClick={()=>navigate('/dashboard/events')} className="card"
          style={{ cursor:'pointer', transition:'all 0.2s', padding:'1.1rem', display:'flex', flexDirection:'column', justifyContent:'space-between', minHeight:126 }}
          onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(196,130,106,0.4)'; e.currentTarget.style.transform='translateY(-2px)' }}
          onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.transform='none' }}>
          <div>
            <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(196,130,106,0.13)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'0.7rem' }}>
              <Calendar size={17} color="#C4826A"/>
            </div>
            <p style={{ fontFamily:'Gibs, serif', fontSize:'1.75rem', color:'#1A1A1A', margin:'0 0 0.05rem', fontWeight:400, lineHeight:1 }}>{events?.length||0}</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#9B9B9B', margin:0, fontWeight:500 }}>planned events</p>
          </div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#C4826A', margin:'0.75rem 0 0', fontWeight:700, letterSpacing:'0.01em' }}>View all →</p>
        </div>

        {/* ── Guests card ── */}
        <div onClick={()=>navigate('/dashboard/guests')} className="card"
          style={{ cursor:'pointer', transition:'all 0.2s', padding:'1.1rem', display:'flex', flexDirection:'column', justifyContent:'space-between', minHeight:126 }}
          onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(232,176,154,0.5)'; e.currentTarget.style.transform='translateY(-2px)' }}
          onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.transform='none' }}>
          <div>
            <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(242,213,197,0.45)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'0.7rem' }}>
              <Users size={17} color="#C4826A"/>
            </div>
            <p style={{ fontFamily:'Gibs, serif', fontSize:'1.75rem', color:'#1A1A1A', margin:'0 0 0.05rem', fontWeight:400, lineHeight:1 }}>{confirmedCount}</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0 0 0.6rem', fontWeight:400 }}>of {guestCount} invited</p>
            <div style={{ height:3, background:'#F3F0EA', borderRadius:100, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${guestCount>0?Math.min((confirmedCount/guestCount)*100,100):0}%`, background:'linear-gradient(to right,#E8B09A,#C4826A)', borderRadius:100, transition:'width 0.6s' }}/>
            </div>
          </div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#9B9B9B', margin:'0.5rem 0 0', fontWeight:500 }}>confirmed</p>
        </div>

        {/* ── Budget card ── */}
        <div onClick={()=>navigate('/dashboard/budget')} className="card"
          style={{ cursor:'pointer', transition:'all 0.2s', padding:'1.1rem', display:'flex', flexDirection:'column', justifyContent:'space-between', minHeight:126 }}
          onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(212,169,122,0.4)'; e.currentTarget.style.transform='translateY(-2px)' }}
          onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.transform='none' }}>
          <div>
            <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'#F5E9D0', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'0.7rem' }}>
              <DollarSign size={17} color="#C4826A"/>
            </div>
            <p style={{ fontFamily:'Gibs, serif', fontSize:'1.4rem', color:'#1A1A1A', margin:'0 0 0.05rem', fontWeight:400, lineHeight:1.15 }}>{currency}{budgetSpent.toLocaleString()}</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0 0 0.6rem', fontWeight:400 }}>
              {totalBudget>0 ? `of ${currency}${totalBudget.toLocaleString()}` : 'no budget set yet'}
            </p>
            <div style={{ height:3, background:'#F3F0EA', borderRadius:100, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${totalBudget>0?Math.min((budgetSpent/totalBudget)*100,100):0}%`, background:'linear-gradient(to right,#D4A97A,#C4826A)', borderRadius:100, transition:'width 0.6s' }}/>
            </div>
          </div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#9B9B9B', margin:'0.5rem 0 0', fontWeight:500 }}>budget spent</p>
        </div>

        {/* ── Next Up card — sage, action-forward ── */}
        <div onClick={()=>navigate('/dashboard/planning')} className="card"
          style={{ cursor:'pointer', transition:'all 0.2s', padding:'1.1rem', display:'flex', flexDirection:'column', justifyContent:'space-between', minHeight:126, borderColor:'rgba(155,175,155,0.35)', background:'rgba(155,175,155,0.05)' }}
          onMouseEnter={e=>{ e.currentTarget.style.borderColor='#9BAF9B'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.background='rgba(155,175,155,0.09)' }}
          onMouseLeave={e=>{ e.currentTarget.style.borderColor='rgba(155,175,155,0.35)'; e.currentTarget.style.transform='none'; e.currentTarget.style.background='rgba(155,175,155,0.05)' }}>
          <div>
            <div style={{ width:36, height:36, borderRadius:'0.75rem', background:'rgba(155,175,155,0.2)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'0.7rem' }}>
              <span style={{ fontSize:'1rem', lineHeight:1 }}>📋</span>
            </div>
            {nextTasks.length > 0 ? (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem' }}>
                {nextTasks.slice(0,2).map((t,i) => (
                  <p key={i} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#3A3A3A', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.6 }}>· {t}</p>
                ))}
              </div>
            ) : (
              <p style={{ fontFamily:'Gibs, serif', fontSize:'1.2rem', color:'#9BAF9B', margin:'0 0 0.1rem', fontWeight:400 }}>You're all caught up ✓</p>
            )}
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.6rem 0 0.35rem', fontWeight:400 }}>
              {todoProgress.done} of {todoProgress.total} tasks done
            </p>
            <div style={{ height:3, background:'#F3F0EA', borderRadius:100, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${Math.min((todoProgress.done/todoProgress.total)*100,100)}%`, background:'linear-gradient(to right,#9BAF9B,#7A917A)', borderRadius:100, transition:'width 0.6s' }}/>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'0.75rem' }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#7A917A', margin:0, fontWeight:700, letterSpacing:'0.01em' }}>Next Up</p>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'white', fontWeight:700, background:'linear-gradient(135deg,#7A917A,#9BAF9B)', borderRadius:100, padding:'0.3rem 0.875rem', boxShadow:'0 2px 8px rgba(122,145,122,0.35)', letterSpacing:'0.01em' }}>Go →</span>
          </div>
        </div>
      </div>

      {/* Question of the Week — a gentle marriage-prep ritual for the couple */}
      <QuestionOfWeek/>

      {/* Memory Report + Wrapped — renders nothing until the wedding date has passed */}
      <MemoryReportCard/>

      {/* Quick actions */}
      <div>
        <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', color:'#1A1A1A', marginBottom:'0.875rem' }}>Get started</h3>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:'0.625rem' }}>
          {quickActions.map(({ label, desc, to, icon:Icon }) => (
            <button key={label} onClick={()=>navigate(to)} style={{ display:'flex', alignItems:'center', gap:'0.875rem', background:'white', border:'1px solid #E5E0D8', borderRadius:'0.875rem', padding:'0.875rem 1rem', cursor:'pointer', textAlign:'left', transition:'all 0.2s', width:'100%' }}
              onMouseEnter={e=>{ e.currentTarget.style.borderColor='#C4826A'; e.currentTarget.style.transform='translateY(-2px)' }}
              onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.transform='none' }}>
              <div style={{ width:36, height:36, background:'rgba(196,130,106,0.08)', borderRadius:'0.625rem', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon size={16} color="#C4826A"/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:'0 0 0.15rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:0 }}>{desc}</p>
              </div>
              <ChevronRight size={15} color="#BCBCBC" style={{ flexShrink:0 }}/>
            </button>
          ))}
        </div>
      </div>

      {/* Events preview — click goes to My Events page */}
      {events && events.length > 0 && (
        <div>
          <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', color:'#1A1A1A', marginBottom:'0.875rem' }}>Your Events</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
            {events.map(ev => (
              <div key={ev.id} onClick={()=>navigate(`/dashboard/events/${ev.id}`)} className="card" style={{ display:'flex', alignItems:'center', gap:'0.875rem', padding:'0.875rem 1.125rem', cursor:'pointer', transition:'all 0.2s' }}
                onMouseEnter={e=>{ e.currentTarget.style.borderColor='#C4826A'; e.currentTarget.style.transform='translateY(-1px)' }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E0D8'; e.currentTarget.style.transform='none' }}>
                <div style={{ width:34, height:34, background:'rgba(155,175,155,0.12)', borderRadius:'0.5rem', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Clock size={15} color="#9BAF9B"/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.875rem', color:'#1A1A1A', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ev.type}</p>
                  {ev.date && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:'2px 0 0' }}>
                    {new Date(ev.date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}
                  </p>}
                </div>
                {ev.venue && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#BCBCBC', margin:0, flexShrink:0, textAlign:'right', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ev.venue}</p>}
                <ChevronRight size={14} color="#BCBCBC" style={{ flexShrink:0 }}/>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Referral — subtle */}
      {wedding?.referral_code && (
        <div className="card" style={{ padding:'1rem 1.25rem', display:'flex', alignItems:'center', gap:'0.875rem', flexWrap:'wrap' }}>
          <Share2 size={18} color="#C4826A" style={{ flexShrink:0 }}/>
          <div style={{ flex:1, minWidth:160 }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', margin:'0 0 0.15rem' }}>Love Okoiya? Share it 💛</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#6B6B6B', margin:0, lineHeight:1.5 }}>
              Invite an engaged friend with your personal link. If they love planning here as much as you do, they'll thank you for it.
            </p>
          </div>
          <button onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/signup?ref=${wedding.referral_code}`)
            setRefCopied(true); setTimeout(() => setRefCopied(false), 2000)
          }} style={{ display:'flex', alignItems:'center', gap:'0.3rem', background:refCopied?'rgba(155,175,155,0.12)':'rgba(196,130,106,0.08)', border:`1.5px solid ${refCopied?'rgba(155,175,155,0.3)':'rgba(196,130,106,0.2)'}`, borderRadius:100, padding:'0.4rem 0.875rem', cursor:'pointer', flexShrink:0 }}>
            <Copy size={12} color={refCopied?'#7A917A':'#C4826A'}/>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:refCopied?'#7A917A':'#C4826A' }}>{refCopied?'Copied!':'Copy Link'}</span>
          </button>
        </div>
      )}

      {/* Wallet balance — only show if > 0 */}
      {walletBal > 0 && (
        <div style={{ background:'rgba(155,175,155,0.06)', border:'1px solid rgba(155,175,155,0.15)', borderRadius:'0.875rem', padding:'0.75rem 1rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#1A1A1A', margin:0 }}>Okoiya credit</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'#9BAF9B', margin:'0.1rem 0 0' }}>Use it in Okoiya Aisle (coming soon)</p>
          </div>
          <p style={{ fontFamily:'Gibs, serif', fontSize:'1.1rem', color:'#6B8F6B', margin:0, fontWeight:400 }}>₦{walletBal.toLocaleString()}</p>
        </div>
      )}

    </div>
  )
}
